
import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase';
import { collection, query, where, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { MemberModel, StudentExamModel, SiteSettingModel } from '../../../../models';
import { Trophy, Plus, Search } from 'lucide-react';
import StudentExamCard from './StudentExamCard';

interface StudentExamListProps {
    member: MemberModel;
    siteSettings: SiteSettingModel;
    onEdit: (exam: StudentExamModel) => void;
}

const StudentExamList: React.FC<StudentExamListProps> = ({ member, siteSettings, onEdit }) => {
    const [exams, setExams] = useState<StudentExamModel[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        console.log('StudentExamList - member.id:', member.id);

        const q = query(
            collection(db, 'studentExams'),
            where('memberId', '==', member.id)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const list: StudentExamModel[] = [];
            snap.forEach(doc => {
                console.log('Exam doc:', doc.id, doc.data());
                list.push({ id: doc.id, ...doc.data() } as StudentExamModel);
            });
            // Sort by date descending on client side
            list.sort((a, b) => b.date - a.date);
            console.log('Total exams loaded:', list.length);
            setExams(list);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching exams:", err);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [member.id]);

    const handleDelete = async (id: string) => {
        if (!confirm('정말로 이 시험 기록을 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'studentExams', id));
        } catch (error) {
            console.error("Error deleting exam:", error);
            alert("삭제 중 오류가 발생했습니다.");
        }
    };

    const filteredExams = exams.filter(e =>
        e.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            {/* Search Bar */}
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                    type="text"
                    placeholder="시험 명칭으로 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white border border-slate-100 rounded-xl pl-11 pr-4 py-3 text-sm font-medium shadow-sm focus:ring-2 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300"
                />
            </div>

            {/* Exam List */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                    <p className="text-slate-400 font-bold">기록을 불러오는 중입니다...</p>
                </div>
            ) : filteredExams.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                    {filteredExams.map((ex) => (
                        <StudentExamCard
                            key={ex.id}
                            exam={ex}
                            onEdit={onEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            ) : (
                <div className="py-20 text-center bg-white rounded-[3rem] border border-slate-100 shadow-sm border-dashed">
                    <Trophy size={48} className="mx-auto text-slate-100 mb-4" />
                    <h3 className="text-lg font-black text-slate-800">아직 등록된 성적이 없습니다</h3>
                    <p className="text-sm font-bold text-slate-400 mt-1">첫 번째 시험 성적을 등록해 보세요!</p>
                </div>
            )}
        </div>
    );
};

export default StudentExamList;
